import { GeneratedDocument } from '@/models/GeneratedDocument';
import {
  RequestForQuotation,
  UpdateRFQContactDetails,
  UpdateRFQSubmissionRequirements,
} from '@/models/RequestForQuotation';
import { apiClient } from '@/services/api';
import { convertToMillisecondTimestamp } from '@/utils/formatDate';
import { logger } from '@/utils/logger/loggerInstance';

export const searchRequestForQuoteByIssue = async (issueId: string) => {
  const url = `/rfqs/search?issueId=${issueId}`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<RequestForQuotation[]>(url);

  return response.data.map((rfq) => ({
    ...rfq,
    createdAt: convertToMillisecondTimestamp(rfq.createdAt),
    updatedAt: convertToMillisecondTimestamp(rfq.updatedAt),
  }));
};

export const searchRequestForQuoteByProperty = async (propertyId: string) => {
  const url = `/rfqs/search?propertyId=${propertyId}`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<RequestForQuotation[]>(url);

  return response.data.map((rfq) => ({
    ...rfq,
    createdAt: convertToMillisecondTimestamp(rfq.createdAt),
    updatedAt: convertToMillisecondTimestamp(rfq.updatedAt),
  }));
};

export const getRequestForQuote = async (rfqId: string) => {
  const url = `/rfqs/${rfqId}`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<RequestForQuotation>(url);

  return {
    ...response.data,
    createdAt: convertToMillisecondTimestamp(response.data.createdAt),
    updatedAt: convertToMillisecondTimestamp(response.data.updatedAt),
  };
};

export const deleteRequestForQuote = async (rfqId: string) => {
  const url = `/rfqs/${rfqId}`;

  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url);
};

export const updateRFQContact = async (params: {
  rfqId: string;
  data: UpdateRFQContactDetails;
}) => {
  const url = `/rfqs/${params.rfqId}/update-contact`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<RequestForQuotation>(url, params.data);

  return response.data;
};

export const updateRFQSubmissionRequirements = async (params: {
  rfqId: string;
  data: UpdateRFQSubmissionRequirements;
}) => {
  const url = `/rfqs/${params.rfqId}/submission-requirements`;

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<RequestForQuotation>(url, params.data);

  return response.data;
};

export const generatePDF = async (params: { rfqId: string; share: boolean }) => {
  const url = `/rfqs/${params.rfqId}/generate-pdf?share=${params.share}`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{
    generatedDocument: GeneratedDocument;
    downloadUrl: string;
  }>(url);

  return {
    generatedDocument: {
      ...response.data.generatedDocument,
      createdAt: convertToMillisecondTimestamp(response.data.generatedDocument.createdAt),
      updatedAt: convertToMillisecondTimestamp(response.data.generatedDocument.updatedAt),
    },
    downloadUrl: response.data.downloadUrl,
  };
};

export const compileRFQPdfs = async (params: { rfqIds: string[]; share?: boolean }) => {
  const share = params.share ?? false;
  const url = `/rfqs/compile?share=${share}`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{
    generatedDocument: GeneratedDocument;
    downloadUrl: string;
  }>(url, { rfqIds: params.rfqIds });

  return {
    generatedDocument: {
      ...response.data.generatedDocument,
      createdAt: convertToMillisecondTimestamp(response.data.generatedDocument.createdAt),
      updatedAt: convertToMillisecondTimestamp(response.data.generatedDocument.updatedAt),
    },
    downloadUrl: response.data.downloadUrl,
  };
};
